// Dependencies
import React, { FunctionComponent, useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { Controller, useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'
import { zodResolver } from '@hookform/resolvers/zod'

// Components
import { Button } from 'component-library/button'
import { Input } from 'component-library/input'

// Schema
import { StudentFormData, studentValidationSchema } from './lead-form.schema'

// Types
import { LeadFormProps } from './lead-form.types'

// Styles
import {
  ButtonsWrapper,
  CancelButtonElementWrapper,
  CloseIconWrapper,
  ContentSubtitle,
  ContentTitle,
  ContentWrapper,
  InputsWrapper,
  TitleWrapper
} from './lead-form.styles'

export const LeadForm: FunctionComponent<LeadFormProps> = ({
  title,
  subtitle,
  cancelButtonElement,
  closeIconElement,
  onSubmit
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { handleSubmit, control, reset, clearErrors, formState } =
    useForm<StudentFormData>({
      resolver: zodResolver(studentValidationSchema),
      mode: 'onChange',
      defaultValues: {
        student: {
          name: '',
          email: '',
          whatsapp: ''
        }
      }
    })

  useEffect(() => {
    const originalXHR = window.XMLHttpRequest

    function CustomXHR() {
      const xhr = new originalXHR()

      xhr.addEventListener('readystatechange', function () {
        if (xhr.readyState === 4) {
          if (this.responseURL.includes('event-api')) {
            if (this.status === 200) {
              setIsLoading(false)

              const searchParams = window.location.search;
              navigate(`/resultados${searchParams}`);
            }
          }
        }
      })

      return xhr
    }

    window.XMLHttpRequest = CustomXHR as any

    return () => {
      window.XMLHttpRequest = originalXHR
    }
  }, [])

  const handleFormSubmit = (data: any) => {
    onSubmit(data)

    setIsLoading(true)
  }

  const handleClose = () => {
    reset()
    clearErrors()
  }

  return (
    <ContentWrapper id="SISU" onSubmit={handleSubmit(handleFormSubmit)}>
      <CloseIconWrapper onClick={handleClose}>
        {closeIconElement}
      </CloseIconWrapper>

      <TitleWrapper>
        <ContentTitle>{title}</ContentTitle>

        <ContentSubtitle>{subtitle}</ContentSubtitle>
      </TitleWrapper>

      <InputsWrapper>
        <Controller
          name="student.name"
          control={control}
          render={({ onChange, value }) => (
            <Input
              id="name"
              label="Nome"
              placeholder="Digite seu nome"
              value={value}
              onChange={onChange}
              {...(formState.errors?.student?.name?.message && {
                errorMessage: `${formState.errors.student.name.message}`,
                variant: 'error'
              })}
            />
          )}
        />

        <Controller
          name="student.email"
          control={control}
          render={({ onChange, value }) => (
            <Input
              id="email"
              label="E-mail"
              placeholder="Digite seu e-mail"
              value={value}
              onChange={onChange}
              {...(formState.errors?.student?.email?.message && {
                errorMessage: `${formState.errors.student.email.message}`,
                variant: 'error'
              })}
            />
          )}
        />

        <Controller
          name="student.whatsapp"
          control={control}
          render={({ onChange, value }) => (
            <InputMask
              mask="(99) 99999-9999"
              maskChar={null}
              value={value}
              onChange={onChange}
            >
              <Input
                id="whatsapp"
                label="Whatsapp"
                placeholder="Digite seu whatsapp"
                {...(formState.errors?.student?.whatsapp?.message && {
                  errorMessage: `${formState.errors.student.whatsapp.message}`,
                  variant: 'error'
                })}
              />
            </InputMask>
          )}
        />
      </InputsWrapper>

      <ButtonsWrapper>
        <CancelButtonElementWrapper onClick={handleClose}>
          {cancelButtonElement}
        </CancelButtonElementWrapper>

        <Button id="cadastro-sisu" label="Ver resultado" type="submit" isLoading={isLoading} />
      </ButtonsWrapper>
    </ContentWrapper>
  )
}
